import { inject, Injectable } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { type UserDetailQuery_User } from '../../api/user/user.api';
import { ViewerApi } from '../../api/viewer/viewer.api';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceViewerService {
  private viewerApi = inject(ViewerApi);
  private userService = inject(UserService);

  viewerHasAccessToPortfolioApplication() {
    return this.viewerApi.watchViewerHasPortfolioApplication().pipe(map((viewer) => !!viewer?.hasPortfolioApplication));
  }

  viewerWithCardsAndTasks() {
    return this.viewerApi.watchViewerWithCardsAndTasks();
  }

  watchViewerWithAvatar() {
    return this.getViewer().pipe(
      switchMap((user) =>
        this.userService.watchAvatar(user.id as string).pipe(
          map((userWithAvatar) => ({
            ...user,
            ...userWithAvatar,
          })),
        ),
      ),
    );
  }

  getViewer() {
    return this.viewerApi.watchViewer().pipe(take(1));
  }

  getViewerWithProgramRoles() {
    return this.viewerApi.watchViewerWithProgramRoles();
  }

  watchViewer() {
    return this.viewerApi.watchViewer().pipe(
      switchMap((viewer) => this.userService.getUserDetail(viewer.id as string)),
      tap((viewer) => this.setBugsnagUser(viewer)),
    );
  }

  watchViewerCustomSettings() {
    return this.viewerApi.watchViewerCustomSettings().pipe(map((viewer) => viewer));
  }

  // store a viewer custom setting in the user's account
  setViewerCustomSetting(key: string, value: any) {
    return this.getViewer().pipe(
      switchMap((viewer) => {
        if (!viewer) {
          return of(null);
        }

        const customSettings = { ...(viewer.customSettings ?? {}) };
        // set key value
        customSettings[key] = value;

        // update backend
        return this.userService.updateUser({
          userId: viewer.id as string,
          customSettings,
        });
      }),
    );
  }

  // get a viewer custom setting
  getViewerCustomSetting(key: string) {
    return this.getViewer().pipe(
      map((viewer) => {
        if (!viewer || !viewer.customSettings) {
          return undefined;
        }

        // try to return the requested value
        return key.split('.').reduce((prev, curr) => {
          return prev ? prev[curr] : undefined;
        }, viewer.customSettings);
      }),
    );
  }

  // store a ui preference
  storeUiPreference(preferenceKey: string, value: string | boolean | number) {
    // try to get our preferences object or get an empty one
    return this.getViewerCustomSetting('ui-preferences-web').pipe(
      switchMap((preferences) => {
        if (!preferences) {
          preferences = {};
        }

        // store the custom settings in user
        return this.setViewerCustomSetting('ui-preferences-web', {
          ...preferences,
          [preferenceKey]: value,
        });
      }),
    );
  }

  // get a ui preference
  getUiPreference(preferenceKey: string) {
    // the ui preference can either be stored in account settings or in localstorage
    return this.getViewerCustomSetting('ui-preferences-web.' + preferenceKey);
  }

  private setBugsnagUser(viewer: UserDetailQuery_User) {
    if (!viewer) {
      return;
    }
    // set user info on bugsnag
    Bugsnag.addMetadata('user', {
      workspaceDashboardRoles: viewer.workspaceDashboardRoles
        ? // @ts-expect-error TS2345 argument not assignable
          this.userService.mapToWorkspaceDashboardRoles(viewer.workspaceDashboardRoles)
        : undefined,
    });
  }
}
